// Shared by Cloud Functions and the frontend (imported there as @shared).
// Pro contracts: the money rules, what players ask for, and payrolls.
// College leagues have no money.
//
// player.contract: {salary, years}: years left including this season.
// After the season (development) every contract loses a year; at 0 the
// player is expiring and can re-sign or become a free agent.
// team.deadMoney: [{playerId, name, salary, year}]: released players'
// salaries, counted on that season's cap only.

export const MONEY_DEFAULTS = {
  cap: 140000000,
  minSalary: 1200000,
  maxShare: 0.3,
  rosterMin: 12,
  rosterMax: 15,
};
export const MAX_YEARS = 4;

/**
 * The league's money rules (leagues created before contracts get the
 * defaults).
 * @param {object} settings league settings
 * @return {object|null} null for college
 */
export function moneyRules(settings) {
  if (settings?.type !== "pro") return null;
  return {...MONEY_DEFAULTS, ...settings.money};
}

/**
 * The season a team's cap counts toward now: the coming one during the
 * offseason.
 * @param {object} league
 * @return {number}
 */
export function capYear(league) {
  const s = league.season;
  return s.phase === "offseason" || s.phase === "complete" ?
    s.year + 1 : s.year;
}

/**
 * @param {object} rules moneyRules
 * @return {number} the largest salary allowed
 */
export function maxSalary(rules) {
  return Math.round(rules.cap * rules.maxShare / 100000) * 100000;
}

/**
 * What a player asks for: by ability (about $1.2M at overall 50, $5M at
 * 60, $20M at 70, the maximum by the high 70s) and age (young players
 * a little more, players past 30 less), plus how many years he wants.
 * Uses visible ratings only.
 * @param {object} player
 * @param {object} rules moneyRules
 * @return {object} {salary, years}
 */
export function askingContract(player, rules) {
  const age = player.age ?? 27;
  let salary = rules.minSalary * Math.exp(0.143 * (player.overall - 50));
  if (age <= 24) salary *= 1.05;
  if (age > 30) salary *= Math.max(0.5, 1 - 0.08 * (age - 30));
  salary = Math.min(maxSalary(rules), Math.max(rules.minSalary, salary));
  const years = age <= 25 ? 4 : age <= 29 ? 3 : age <= 32 ? 2 : 1;
  return {salary: roundSalary(salary), years};
}

/**
 * @param {number} x dollars
 * @return {number} to the nearest $100,000
 */
export function roundSalary(x) {
  return Math.round(x / 100000) * 100000;
}

/**
 * A team's payroll for a season: contracts of players under contract plus
 * that season's dead money.
 * @param {object} team team document
 * @param {number} year
 * @param {object} [opts] {expiring: count players whose contract has
 *   run out (default false)}
 * @return {number}
 */
export function payroll(team, year, {expiring = false} = {}) {
  let total = 0;
  for (const p of Object.values(team.players ?? {})) {
    const c = p.contract;
    if (c && (c.years > 0 || expiring)) total += c.salary;
  }
  for (const d of team.deadMoney ?? []) {
    if (d.year === year) total += d.salary;
  }
  return total;
}

/**
 * @param {number} dollars
 * @return {string} "$12.4M" / "$850K"
 */
export function formatMoney(dollars) {
  if (Math.abs(dollars) >= 1000000) {
    return `$${(dollars / 1000000).toFixed(1)}M`;
  }
  return `$${Math.round(dollars / 1000)}K`;
}
